import { css, html, nothing, type TemplateResult } from 'lit'
import { csColorAction, csColorBorder, csColorFunctional, csColorText } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { MODE_LABELS, isMobileConfigMode, layoutKindFor, type ConfigMode, type Section } from 'components/shared/product-tile-configurator/editor-state.js'
import { areaFor, type DynamicArea, type TileConfiguratorActionsContextType } from 'components/shared/product-tile-configurator/context.js'

export type PanelHeaderKind = 'area' | 'zone' | 'widget'

export interface Breadcrumb {
  label: string
  onSelect?: () => void
}

const KIND_LABELS: Record<PanelHeaderKind, string> = {
  area: 'Area',
  zone: 'Zone',
  widget: 'Widget',
}

export const AREA_LABELS: Record<DynamicArea, string> = {
  productInfo: 'Product info',
  orderActions: 'Order actions',
}

export const IMAGE_AREA_LABEL = 'Image'

export const viewCrumb = (mode: ConfigMode, mobileMode: boolean): Breadcrumb => ({
  label: `${MODE_LABELS[layoutKindFor(mode)]} ${mobileMode || isMobileConfigMode(mode) ? 'mobile' : 'desktop'}`,
})

// above/below are always exactly one zone named after their slot; every other zone (dynamic or
// one of the 8 image overlay places) carries its own name.
export const zoneLabelFor = (section: Section): string => {
  if (section.dropAreaType === 'image-flow') return section.id === 'above' ? 'Above' : 'Below'
  return section.name
}

// The area a zone belongs to, as a clickable crumb: productInfo/orderActions select that area;
// image overlay places and above/below all belong to the image area.
export const areaCrumbFor = (mode: ConfigMode, section: Section, actions: TileConfiguratorActionsContextType): Breadcrumb => {
  const area = areaFor(section.dropAreaType)
  return area
    ? { label: AREA_LABELS[area], onSelect: () => actions.selectArea(mode, area) }
    : { label: IMAGE_AREA_LABEL, onSelect: () => actions.selectImage(mode) }
}

export const zoneCrumbFor = (mode: ConfigMode, section: Section, actions: TileConfiguratorActionsContextType): Breadcrumb => ({
  label: zoneLabelFor(section),
  onSelect: () => actions.selectWidget({ mode, sectionId: section.id, widgetId: null }),
})

// Shared by both right-panel components (zone panel: area/zone, sidebar: widget). The last crumb
// is always the current selection itself, so it's never clickable.
export interface PanelTab<T extends string> {
  value: T
  label: string
  // Small dot after the label — e.g. the Visibility tab when the widget has visibility conditions.
  indicator?: string | undefined
}

// Tabs sit inside the header, flush on its bottom border (see .sidebar-header.has-tabs), so the
// active tab's underline lands exactly on that border, like the design.
export const renderPanelTabs = <T extends string>(tabs: PanelTab<T>[], active: T, idPrefix: string, onSelect: (value: T) => void) => html`
  <div class="panel-tabs" role="tablist">
    ${tabs.map(tab => html`
      <button
        type="button"
        role="tab"
        id="${idPrefix}-tab-${tab.value}"
        class="panel-tab panel-tab-${tab.value} ${tab.value === active ? 'active' : ''}"
        aria-selected=${tab.value === active ? 'true' : 'false'}
        aria-controls="${idPrefix}-panel-${tab.value}"
        @click=${() => onSelect(tab.value)}>
        <cse-text variant="body-s">${tab.label}</cse-text>
        ${tab.indicator ? html`<span class="panel-tab-indicator" title=${tab.indicator} aria-label=${tab.indicator}></span>` : nothing}
      </button>
    `)}
  </div>
`

export const renderPanelHeader = (crumbs: Breadcrumb[], title: string, kind: PanelHeaderKind, tabs?: TemplateResult) => {
  const current: Breadcrumb = { label: title }
  const trail = [...crumbs, current]
  return html`
    <div class="sidebar-header ${tabs ? 'has-tabs' : ''}">
      <nav class="breadcrumbs" aria-label="Selection">
        <ol>
          ${trail.map((crumb, index) => html`
            <li>
              ${index > 0 ? html`<cse-text class="breadcrumb-separator" variant="caption-m" aria-hidden="true">›</cse-text>` : nothing}
              ${crumb.onSelect
                ? html`<button type="button" class="breadcrumb-link" @click=${crumb.onSelect}><cse-text variant="caption-m">${crumb.label}</cse-text></button>`
                : html`<cse-text class="breadcrumb-text ${index === trail.length - 1 ? 'breadcrumb-current' : ''}" variant="caption-m" aria-current=${index === trail.length - 1 ? 'location' : nothing}>${crumb.label}</cse-text>`}
            </li>
          `)}
        </ol>
      </nav>
      <div class="sidebar-header-title-row">
        <span class="title-dot title-dot-${kind}"></span>
        <cse-text class="sidebar-header-title" variant="heading-5">${title}</cse-text>
        <cse-text class="sidebar-header-kind" variant="caption-s">${KIND_LABELS[kind]}</cse-text>
      </div>
      ${tabs ?? nothing}
    </div>
  `
}

export const panelHeaderStyle = css`
  .sidebar-header {
    display: flex;
    flex-direction: column;
    gap: ${csSpacing.spacing05};
    padding: ${csSpacing.spacing06};
    border-bottom: ${csBorder.width} solid ${csColorBorder.light};
  }
  .sidebar-header.has-tabs {
    padding-bottom: 0;
  }
  .panel-tabs {
    display: flex;
    gap: ${csSpacing.spacing07};
    margin-top: ${csSpacing.spacing03};
  }
  .panel-tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: ${csSpacing.spacing04};
    padding: ${csSpacing.spacing04} 0 ${csSpacing.spacing05};
    border: none;
    background: transparent;
    cursor: pointer;
    --cse-text-color: ${csColorText.muted};
    --cs-font-body-s-font-size: 15px;
    --cs-font-body-s-font-size-mobile: 15px;
  }
  .panel-tab:hover {
    --cse-text-color: ${csColorText.body};
  }
  .panel-tab.active {
    --cse-text-color: ${csColorText.headings};
    --cs-font-body-s-font-weight: 600;
  }
  /* Underline sits over the header's own bottom border (margin-bottom: -border width), in the
     widget level's color (see .title-dot-widget). */
  .panel-tab.active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(-1 * ${csBorder.width});
    height: 2px;
    background: ${csColorAction.linkPrimary};
  }
  .panel-tab-indicator {
    flex: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${csColorFunctional.warning};
  }
  .breadcrumbs ol {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .breadcrumbs li {
    display: inline-flex;
    align-items: center;
    min-width: 0;
  }
  .breadcrumbs cse-text {
    --cs-font-caption-m-font-size: 12px;
    --cs-font-caption-m-font-size-mobile: 12px;
  }
  .breadcrumb-separator {
    padding: 0 ${csSpacing.spacing04};
    --cse-text-color: ${csColorText.muted};
  }
  .breadcrumb-text {
    --cse-text-color: ${csColorText.muted};
  }
  .breadcrumb-current {
    --cse-text-color: ${csColorAction.linkSecondary};
  }
  .breadcrumb-link {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    --cse-text-color: ${csColorAction.linkSecondary};
  }
  .breadcrumb-link:hover {
    --cse-text-color: ${csColorAction.linkSecondaryHover};
    text-decoration: underline;
  }
  .sidebar-header-title-row {
    display: flex;
    align-items: center;
    gap: ${csSpacing.spacing05};
  }
  .sidebar-header-title {
    flex: 1;
    min-width: 0;
  }
  .sidebar-header-kind {
    flex: none;
    color: ${csColorText.muted};
  }
  /* Same 3-way color scheme as the canvas's selection outlines (see tile-configurator-canvas.style.ts's
     "Selection outline styling" note) — the dot's color alone says which level is selected. */
  .title-dot {
    flex: none;
    width: 10px;
    height: 16px;
    border-radius: 1px;
  }
  .title-dot-area {
    background: ${csColorFunctional.highlight};
  }
  .title-dot-zone {
    background: ${csColorFunctional.success};
  }
  .title-dot-widget {
    background: ${csColorAction.linkPrimary};
  }
`
